import api from '../api';

export const DOCUMENT_FORM_SCOPE_SEPARATOR = '::';

/**
 * Normalize error into a consistent shape so callers can handle uniformly.
 * @param {*} error
 * @param {string} context - Human-readable description of what failed (used in console.error)
 * @returns {{ success: false, message: string, originalError?: * }}
 */
export const normalizeError = (error, context = 'API Error') => {
  const message =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    'An unexpected error occurred';
  console.error(`${context}:`, message, error);
  return { success: false, message, originalError: error };
};

export const buildDocumentFormStorageKey = (documentCode, scopeKey) => {
  if (!documentCode) return '';
  return scopeKey ? `${scopeKey}${DOCUMENT_FORM_SCOPE_SEPARATOR}${documentCode}` : documentCode;
};

export const parseDocumentFormStorageKey = (storageKey = '') => {
  if (!storageKey.includes(DOCUMENT_FORM_SCOPE_SEPARATOR)) {
    return {
      documentCode: storageKey,
      scopeKey: '',
      storageKey,
    };
  }

  const [scopeKey, ...documentCodeParts] = storageKey.split(DOCUMENT_FORM_SCOPE_SEPARATOR);
  return {
    documentCode: documentCodeParts.join(DOCUMENT_FORM_SCOPE_SEPARATOR),
    scopeKey,
    storageKey,
  };
};

/**
 * Audit Findings API Service
 * Handles all API calls related to audit findings and corrective actions
 */

/**
 * Get all findings for a project
 * @param {number} projectId
 * @param {object} filters - { severity, status, standard, searchText }
 * @returns {Promise<array>}
 */
export const getFindings = async (projectId, filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.severity) params.append('severity', filters.severity);
    if (filters.status) params.append('status', filters.status);
    if (filters.standard) params.append('standard', filters.standard);
    if (filters.searchText) params.append('search', filters.searchText);

    const response = await api.get(`/audit/findings/projects/${projectId}`, { params });
    return response.data;
  } catch (error) {
    return normalizeError(error, 'getFindings');
  }
};

/**
 * Get a single finding by ID
 * @param {number} findingId
 * @returns {Promise<object>}
 */
export const getFinding = async (findingId) => {
  try {
    const response = await api.get(`/audit/findings/${findingId}`);
    return response.data;
  } catch (error) {
    return normalizeError(error, 'getFinding');
  }
};

/**
 * Create a new finding
 * @param {number} projectId
 * @param {object} data - Finding data
 * @returns {Promise<object>}
 */
export const createFinding = async (projectId, data) => {
  try {
    const response = await api.post(`/audit/findings/projects/${projectId}`, data);
    return response.data;
  } catch (error) {
    return normalizeError(error, 'createFinding');
  }
};

/**
 * Update an existing finding
 * @param {number} findingId
 * @param {object} data - Updated finding data
 * @returns {Promise<object>}
 */
export const updateFinding = async (findingId, data) => {
  try {
    const response = await api.put(`/audit/findings/${findingId}`, data);
    return response.data;
  } catch (error) {
    return normalizeError(error, 'updateFinding');
  }
};

/**
 * Delete a finding
 * @param {findingId} number
 * @returns {Promise<void>}
 */
export const deleteFinding = async (findingId) => {
  try {
    await api.delete(`/audit/findings/${findingId}`);
  } catch (error) {
    return normalizeError(error, 'deleteFinding');
  }
};

/**
 * Add a corrective action to a finding
 * @param {number} findingId
 * @param {object} data - Corrective action data
 * @returns {Promise<object>}
 */
export const addCorrectiveAction = async (findingId, data) => {
  try {
    const response = await api.post(`/audit/findings/${findingId}/corrective-actions`, data);
    return response.data;
  } catch (error) {
    return normalizeError(error, 'addCorrectiveAction');
  }
};

/**
 * Update a corrective action
 * @param {number} findingId
 * @param {number} caId - Corrective action ID
 * @param {object} data - Updated CA data
 * @returns {Promise<object>}
 */
export const updateCorrectiveAction = async (findingId, caId, data) => {
  try {
    const response = await api.put(
      `/audit/findings/${findingId}/corrective-actions/${caId}`,
      data
    );
    return response.data;
  } catch (error) {
    return normalizeError(error, 'updateCorrectiveAction');
  }
};

/**
 * Delete a corrective action
 * @param {number} findingId
 * @param {number} caId - Corrective action ID
 * @returns {Promise<void>}
 */
export const deleteCorrectiveAction = async (findingId, caId) => {
  try {
    await api.delete(`/audit/findings/${findingId}/corrective-actions/${caId}`);
  } catch (error) {
    return normalizeError(error, 'deleteCorrectiveAction');
  }
};

/**
 * Export findings data
 * @param {number} projectId
 * @returns {Promise<object>} - Contains findings and metadata for export
 */
export const exportFindings = async (projectId) => {
  try {
    const response = await api.get(`/audit/findings/projects/${projectId}/export`);
    return response.data;
  } catch (error) {
    return normalizeError(error, 'exportFindings');
  }
};

/**
 * Get statistics for findings dashboard
 * @param {number} projectId
 * @returns {Promise<object>}
 */
export const getFindingsStatistics = async (projectId) => {
  try {
    const response = await api.get(`/audit/findings/projects/${projectId}/statistics`);
    return response.data;
  } catch (error) {
    return normalizeError(error, 'getFindingsStatistics');
  }
};

/**
 * Save document form data (create or update)
 * @param {number} projectId
 * @param {{ documentCode, category, formDataJson, scopeKey?: string }} data
 * @returns {Promise<object>}
 */
export const saveDocumentFormData = async (projectId, data) => {
  try {
    const payload = {
      ...data,
      documentCode: buildDocumentFormStorageKey(data?.documentCode, data?.scopeKey),
    };
    const response = await api.post(`/audit/projects/${projectId}/documents/form-data`, payload);
    const parsedKey = parseDocumentFormStorageKey(response?.data?.documentCode || payload.documentCode);
    return {
      ...response.data,
      documentCode: parsedKey.documentCode,
      scopeKey: parsedKey.scopeKey,
      storageDocumentCode: parsedKey.storageKey,
      formDataKey: parsedKey.storageKey,
    };
  } catch (error) {
    return normalizeError(error, 'saveDocumentFormData');
  }
};

export const exportDocumentFormData = async (projectId, documentCode, scopeKey, format = 'pdf') => {
  const storageDocumentCode = buildDocumentFormStorageKey(documentCode, scopeKey);
  const response = await api.get(`/audit/projects/${projectId}/documents/form-data/export`, {
    params: { documentCode: storageDocumentCode, format },
    responseType: 'blob',
  });
  const disposition = response.headers?.['content-disposition'] || '';
  const fileName = disposition.match(/filename="?([^";]+)"?/i)?.[1]
    || `${String(documentCode || 'audit-form').replace(/[^A-Za-z0-9._-]/g, '_')}.${format}`;
  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Load saved document form data for a project
 * @param {number} projectId
 * @param {string} [category]
 * @returns {Promise<array>}
 */
export const loadDocumentFormData = async (projectId, category) => {
  try {
    const params = category ? { category } : {};
    const response = await api.get(`/audit/projects/${projectId}/documents/form-data`, { params });
    return (response.data || []).map((entry) => {
      const parsedKey = parseDocumentFormStorageKey(entry?.documentCode || '');
      return {
        ...entry,
        documentCode: parsedKey.documentCode,
        scopeKey: parsedKey.scopeKey,
        storageDocumentCode: parsedKey.storageKey,
        formDataKey: parsedKey.storageKey,
      };
    });
  } catch (error) {
    return normalizeError(error, 'loadDocumentFormData');
  }
};

// ============== STANDARDS & REQUIREMENTS API ==============

const unwrapAuditPayload = (payload) => {
  if (payload == null) return payload;
  if (Array.isArray(payload)) return payload;
  if (payload?.data != null) return payload.data;
  return payload;
};

const normalizeRequirementsPayload = (payload, standardId) => {
  const data = unwrapAuditPayload(payload) || {};
  return {
    ...data,
    standardId: data?.standardId ?? standardId,
    clauses: Array.isArray(data?.clauses) ? data.clauses : [],
    annexes: Array.isArray(data?.annexes) ? data.annexes : [],
  };
};

const normalizeLegacyRequirementRows = (rows, type) => {
  const list = Array.isArray(rows) ? rows : [];
  return list
    .map((row) => {
      const code = String(row?.code || row?.clausul || row?.annex || row?.name || '').trim();
      if (!code) return null;
      return {
        id: row?.id,
        type,
        code,
        name: String(row?.name || '').trim(),
        description: String(row?.description || '').trim(),
      };
    })
    .filter(Boolean);
};

const loadRequirementsFromLegacyMaster = async (standardId) => {
  const safeStandardId = encodeURIComponent(String(standardId));
  const [clausesResult, annexesResult] = await Promise.allSettled([
    api.get(`/md/standards/${safeStandardId}/clauses`),
    api.get(`/md/standards/${safeStandardId}/annexes`),
  ]);

  const clauses = clausesResult.status === 'fulfilled'
    ? normalizeLegacyRequirementRows(unwrapAuditPayload(clausesResult.value?.data), 'clause')
    : [];
  const annexes = annexesResult.status === 'fulfilled'
    ? normalizeLegacyRequirementRows(unwrapAuditPayload(annexesResult.value?.data), 'annex')
    : [];

  return {
    standardId: String(standardId),
    clauses,
    annexes,
  };
};

/**
 * Get all standards
 * @param {boolean} includeVersions - include standard version/name metadata for label->id mapping
 * @returns {Promise<array>}
 */
export const getStandards = async (includeVersions = false) => {
  try {
    const response = await api.get('/audit/standards', {
      params: { includeVersions: Boolean(includeVersions) }
    });
    const data = unwrapAuditPayload(response.data);
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.content)) return data.content;
    if (Array.isArray(data?.rows)) return data.rows;
    return [];
  } catch (error) {
    return normalizeError(error, 'getStandards');
  }
};

/**
 * Get requirements (clauses/annexes) for a standard
 * @param {string|number} standardId
 * @returns {Promise<object>} - { standardId, clauses: [...], annexes: [...] }
 */
export const getStandardRequirements = async (standardId) => {
  const safeStandardId = encodeURIComponent(String(standardId));
  try {
    const response = await api.get(`/audit/standards/${safeStandardId}/requirements`);
    const normalized = normalizeRequirementsPayload(response.data, standardId);

    // Some environments keep master data in /api/md/standards/* instead of finding_requirements.
    // If grouped audit endpoint returns no rows, fallback to legacy master endpoints.
    if ((normalized.clauses || []).length === 0 && (normalized.annexes || []).length === 0) {
      return loadRequirementsFromLegacyMaster(standardId);
    }

    return normalized;
  } catch (error) {
    console.warn('Primary requirements endpoint failed, fallback to master clauses/annexes:', error?.message || error);
    return loadRequirementsFromLegacyMaster(standardId);
  }
};

/**
 * Get requirements filtered by type
 * @param {string|number} standardId
 * @param {string} type - "clause" or "annex"
 * @returns {Promise<array>}
 */
export const getRequirementsFilteredByType = async (standardId, type) => {
  try {
    const response = await api.get(`/audit/standards/${standardId}/requirements/filter`, {
      params: { type }
    });
    const data = unwrapAuditPayload(response.data);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    return normalizeError(error, 'getRequirementsFilteredByType');
  }
};

/**
 * Get a single requirement by code
 * @param {string|number} standardId
 * @param {string} code
 * @returns {Promise<object>}
 */
export const getRequirementByCode = async (standardId, code) => {
  try {
    const response = await api.get(`/audit/standards/${standardId}/requirements/${code}`);
    return unwrapAuditPayload(response.data) || null;
  } catch (error) {
    return normalizeError(error, 'getRequirementByCode');
  }
};

/**
 * @returns {Promise<array>}
 */
export const getFindingCategories = async () => {
  try {
    const response = await api.get('/audit/finding-categories');
    return response.data;
  } catch (error) {
    return normalizeError(error, 'getFindingCategories');
  }
};

/**
 * Get finding category by ID
 * @param {number} id
 * @returns {Promise<object>}
 */
export const getFindingCategoryById = async (id) => {
  try {
    const response = await api.get(`/audit/finding-categories/${id}`);
    return response.data;
  } catch (error) {
    return normalizeError(error, 'getFindingCategoryById');
  }
};

/**
 * Create a new finding category
 * @param {object} categoryData
 * @returns {Promise<object>}
 */
export const createFindingCategory = async (categoryData) => {
  try {
    const response = await api.post('/audit/finding-categories', categoryData);
    return response.data;
  } catch (error) {
    return normalizeError(error, 'createFindingCategory');
  }
};

/**
 * Update a finding category
 * @param {number} id
 * @param {object} categoryData
 * @returns {Promise<object>}
 */
export const updateFindingCategory = async (id, categoryData) => {
  try {
    const response = await api.put(`/audit/finding-categories/${id}`, categoryData);
    return response.data;
  } catch (error) {
    return normalizeError(error, 'updateFindingCategory');
  }
};

/**
 * Delete a finding category
 * @param {number} id
 * @returns {Promise<object>}
 */
export const deleteFindingCategory = async (id) => {
  try {
    const response = await api.delete(`/audit/finding-categories/${id}`);
    return response.data;
  } catch (error) {
    return normalizeError(error, 'deleteFindingCategory');
  }
};;
